import {
  ChangeDetectorRef,
  Directive,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { connectState, InputObservable } from '@y42/angular/util';
import { UserColorSchemeService } from '@y42/browser/user-color-scheme';
import { combineLatest, debounceTime, distinctUntilChanged, map, Observable, startWith, tap } from 'rxjs';
import { MonacoEditorBase, MONACO_SETTINGS } from './monaco-editor-base';
import { monacoColorThemes } from './monaco.helper';
import { sqlDarkTheme } from './themes/sql-dark-theme';
import { sqlLightTheme } from './themes/sql-light-theme';

const DEFAULT_DIFF_EDITOR_OPTIONS = {
  ...MONACO_SETTINGS,
  readOnly: true,
};

export type LineChange = monaco.editor.ILineChange;
type EditorOptions = monaco.editor.IDiffEditorConstructionOptions;

@UntilDestroy()
@Directive({
  selector: '[y42MonacoDiffEditor]',
  exportAs: 'y42MonacoDiffEditor',
})
export class MonacoDiffEditorDirective
  extends MonacoEditorBase<monaco.editor.IStandaloneDiffEditor>
  implements OnInit, OnChanges
{
  private readonly userColorSchemeService = inject(UserColorSchemeService);

  private readonly cdRef = inject(ChangeDetectorRef);

  @Input()
  @InputObservable()
  options?: Partial<EditorOptions>;
  options$!: Observable<Partial<EditorOptions>>;

  @Input() model: monaco.editor.IDiffEditorModel | null = null;

  @Input() original: string | null | undefined = null;
  @Input() modified: string | null | undefined = null;

  @Output() diffUpdated = new EventEmitter<LineChange[] | null>();

  get modifiedValue() {
    return this.editor ? this.editor.getModifiedEditor().getValue() : '';
  }

  readonly theme$ = this.userColorSchemeService.hasDarkMode$.pipe(
    map((hasDarkMode) => (hasDarkMode ? monacoColorThemes.sql.dark : monacoColorThemes.sql.light)),
    startWith(monacoColorThemes.sql.light),
    distinctUntilChanged(),
  );

  readonly editorOptions$ = combineLatest([this.options$, this.theme$]).pipe(
    map(
      ([options, theme]) =>
        ({
          ...DEFAULT_DIFF_EDITOR_OPTIONS,
          ...options,
          theme,
        } satisfies EditorOptions as EditorOptions),
    ),
  );

  readonly state = connectState(this, {
    editorOptions: this.editorOptions$,
  });

  async init() {
    this.editor = monaco.editor.createDiffEditor(this.host.nativeElement, this.state.editorOptions);
    this.editor.onDidUpdateDiff(() => {
      this.diffUpdated.emit(this.editor.getLineChanges());
    });
    this.setModel();
    this.defineThemes();
    this.updateEditorOptionsWhenOptionsChange().pipe(untilDestroyed(this)).subscribe();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.model || changes.original || changes.modified) {
      this.setModel();
    }
  }

  reset() {
    this.setModel();
  }

  private async setModel() {
    if (this.original && this.modified && this.model) {
      console.warn(`Please use either "model" or "original+modified" input properties.`);
    }

    const model =
      isNotNullOrUndefined(this.original) && isNotNullOrUndefined(this.modified)
        ? await this.monacoLoader.createDiffModel(this.original, this.modified, this.languageId)
        : this.model;

    this.editor?.setModel(model);
    this.cdRef.markForCheck();
  }

  private updateEditorOptionsWhenOptionsChange() {
    return this.editorOptions$.pipe(
      debounceTime(0),
      tap((options) => this.updateOptions(options)),
    );
  }

  private updateOptions(options: EditorOptions) {
    this.editor?.updateOptions(options);
    this.cdRef.markForCheck();
  }

  private defineThemes() {
    monaco.editor.defineTheme(monacoColorThemes.sql.light, sqlLightTheme);
    monaco.editor.defineTheme(monacoColorThemes.sql.dark, sqlDarkTheme);
  }
}

function isNotNullOrUndefined<T>(value: T | null | undefined): value is T {
  return value !== null || value !== undefined;
}
